import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { defaultPfpUrl, defaultGroupPfpUrl } from "../../utils";
import {
  createChatMember,
  deleteChatMember,
  deleteChat,
  updateChatMember,
  getChats,
  getOrganizationChats,
} from "../../../api/api";
import MultiUserSearch from "../MultiUserSearch";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../ui/tabs";
import { ScrollArea } from "../../ui/scroll-area";
import { Separator } from "../../ui/separator";
import { Button } from "../../ui/button";
import { Checkbox } from "../../ui/checkbox";
import { Avatar, AvatarFallback, AvatarImage } from "../../ui/avatar";
import { cn } from "../../../lib/utils";

/**
 * MemberListItem component - Displays a single member with optional delete button.
 * @param {Object} props
 * @returns {JSX.Element}
 */
function MemberListItem({
  member,
  canRemove,
  currentUserId,
  onConfirmRemove,
}) {
  const isCurrentUser = member.user_id === currentUserId;

  const handleRemoveClick = () => {
    if (canRemove) {
      onConfirmRemove(member);
    }
  };

  return (
    <div className="group flex items-center justify-between rounded-md px-2 py-2 transition-colors hover:bg-ui-muted">
      <Link
        to={`/profile/${member.user_id}`}
        className="flex min-w-0 flex-1 items-center hover:opacity-90"
      >
        <Avatar className="mr-3 h-10 w-10 shrink-0">
          <AvatarImage
            src={member.pfp_url || member.pfp || defaultPfpUrl}
            alt={member.name || member.username || "User"}
            onError={(e) => {
              e.target.src = defaultPfpUrl;
            }}
          />
          <AvatarFallback>
            {(member.name || member.username || "?").slice(0, 2).toUpperCase()}
          </AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1">
          <div className="truncate font-medium text-ui-foreground">
            {member.name || member.username || "User"}
            {isCurrentUser && (
              <span className="ml-2 text-sm font-normal text-ui-muted-foreground">
                (You)
              </span>
            )}
          </div>
          {member.role && (
            <div className="truncate text-sm capitalize text-ui-muted-foreground">
              {member.role}
            </div>
          )}
        </div>
      </Link>
      {canRemove && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="shrink-0 text-delete opacity-0 transition-opacity hover:bg-highlight hover:text-delete group-hover:opacity-100"
          title="Remove member"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleRemoveClick();
          }}
        >
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
            />
          </svg>
        </Button>
      )}
    </div>
  );
}

/**
 * MemberList component - Displays list of members with admin controls.
 * @param {Object} props
 * @returns {JSX.Element}
 */
function MemberList({
  members,
  canAddMembers,
  canRemoveMembers,
  currentUserId,
  chatId,
  onMembersUpdate,
  onConfirmRemove,
  candidateUsers,
}) {
  const [showAddMember, setShowAddMember] = useState(false);

  const handleAddMembers = async (selectedUsers) => {
    if (selectedUsers.length === 0) return;

    try {
      let successCount = 0;
      for (const user of selectedUsers) {
        const memberData = {
          user_id: user.id,
          role: "member",
        };
        const res = await createChatMember(chatId, memberData);
        if (res.success) {
          successCount += 1;
        }
      }

      if (successCount > 0) {
        onMembersUpdate();
        setShowAddMember(false);
      } else {
        alert("Failed to add members. Please try again.");
      }
    } catch (error) {
      console.error("Add members error:", error);
      alert("Failed to add members. Please try again.");
    }
  };

  const excludeUsers = members.map((m) => ({
    id: m.user_id,
    username: m.username,
    first_name: m.first_name,
    last_name: m.last_name,
  }));

  return (
    <div className="space-y-1">
      {members.length === 0 ? (
        <div className="py-6 text-center text-sm text-ui-muted-foreground">
          No members found
        </div>
      ) : !showAddMember ? (
        members.map((member) => (
          <MemberListItem
            key={member.id || member.user_id}
            member={member}
            canRemove={canRemoveMembers && member.user_id !== currentUserId}
            currentUserId={currentUserId}
            onConfirmRemove={onConfirmRemove}
          />
        ))
      ) : null}

      {canAddMembers && (
        <>
          {showAddMember ? (
            <div className="mt-2">
              <div className="mb-3 flex items-center justify-between">
                <h5 className="text-sm font-medium text-ui-foreground">Add members</h5>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="text-ui-muted-foreground"
                  onClick={() => setShowAddMember(false)}
                  aria-label="Close add members"
                >
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </Button>
              </div>
              <div className="overflow-hidden rounded-md border border-ui-border">
                <MultiUserSearch
                  userId={currentUserId}
                  onSubmit={handleAddMembers}
                  excludeUsers={excludeUsers}
                  submitText="Add Members"
                  candidateUsers={candidateUsers}
                />
              </div>
            </div>
          ) : (
            <Button
              type="button"
              variant="secondary"
              className="mt-3 w-full"
              onClick={() => setShowAddMember(true)}
            >
              + Add members
            </Button>
          )}
        </>
      )}
    </div>
  );
}

/**
 * ChatInfo component - Modal for chat details and settings.
 * @param {Object} props
 * @param {number} props.chatId - Chat ID
 * @param {Object} props.chatInfo - Chat information
 * @param {number} props.userId - Current user ID
 * @param {function} props.onClose - Callback to close the modal
 * @param {function} props.onChatInfoUpdate - Callback to update chat info after membership changes
 * @param {{ orgId?: number, candidateUsers?: Array<object>, onChatLeft?: function, onChatDeleted?: function }} [props.orgChannelOptions]
 * @param {string} [props.currentUserPfp] - Logged-in user's profile image URL
 */
function ChatInfo({
  chatId,
  chatInfo,
  userId,
  onClose,
  onChatInfoUpdate,
  orgChannelOptions,
  currentUserPfp,
}) {
  const [activeTab, setActiveTab] = useState("info");
  const [imageError, setImageError] = useState(false);
  const [members, setMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [currentUserMember, setCurrentUserMember] = useState(null);
  const [confirmRemoveMember, setConfirmRemoveMember] = useState(null);
  const [confirmLeaveChat, setConfirmLeaveChat] = useState(false);
  const [confirmDeleteChat, setConfirmDeleteChat] = useState(false);

  const isGroupChat =
    chatInfo?.chat_type === "group" || chatInfo?.type === "group";
  const isOrgChannel = Boolean(chatInfo?.org_id) || Boolean(orgChannelOptions?.orgId);
  const isDefaultChannel = Boolean(chatInfo?.is_default);
  const orgId = orgChannelOptions?.orgId || chatInfo?.org_id || 0;

  /**
   * Apply member list state from a chat feed object and sync header/parent chat metadata.
   * @param {object} currentChat
   * @returns {void}
   */
  const applyChatMembers = (currentChat) => {
    const otherMembers = currentChat.other_members || [];
    const userMemberRole = currentChat.user_role;
    const userMember = {
      id: currentChat.member_id,
      user_id: userId,
      name: "You",
      role: userMemberRole,
      pfp: currentUserPfp || defaultPfpUrl,
      muted_until: false,
    };
    const allMembers = [
      userMember,
      ...otherMembers.filter((m) => m.user_id !== userId),
    ];
    setMembers(allMembers);
    const adminStatus =
      userMemberRole === "admin" || userMemberRole === "moderator";
    setIsAdmin(adminStatus);
    setCurrentUserMember(userMember);
    setIsMuted(false);
    if (typeof onChatInfoUpdate === "function") {
      onChatInfoUpdate(currentChat);
    }
  };

  useEffect(() => {
    if (!chatId || !userId || !isGroupChat) return;

    const fetchMembers = async () => {
      setLoadingMembers(true);
      try {
        const res = orgId
          ? await getOrganizationChats(orgId, userId)
          : await getChats(userId);
        if (res.success && res.data) {
          const currentChat = res.data.find((c) => c.id === chatId);
          if (currentChat) {
            applyChatMembers(currentChat);
          }
        }
      } catch (error) {
        console.error("Error fetching members:", error);
      } finally {
        setLoadingMembers(false);
      }
    };

    fetchMembers();
  }, [chatId, userId, isGroupChat, orgId, currentUserPfp]);

  // Keep the open modal member list in sync with realtime chatInfo updates.
  useEffect(() => {
    if (!chatInfo || !isGroupChat || !userId) return;
    const otherMembers = chatInfo.other_members || [];
    const userMember = {
      id: chatInfo.member_id,
      user_id: userId,
      name: "You",
      role: chatInfo.user_role,
      pfp: currentUserPfp || defaultPfpUrl,
      muted_until: false,
    };
    setMembers([
      userMember,
      ...otherMembers.filter((m) => Number(m.user_id) !== Number(userId)),
    ]);
    setCurrentUserMember(userMember);
    setIsAdmin(
      chatInfo.user_role === "admin" || chatInfo.user_role === "moderator"
    );
  }, [
    chatInfo,
    chatInfo?.other_members,
    chatInfo?.member_count,
    chatInfo?.user_role,
    chatInfo?.member_id,
    isGroupChat,
    userId,
    currentUserPfp,
  ]);

  const handleMuteToggle = async (muted) => {
    if (!currentUserMember) return;

    try {
      const memberData = {
        muted_until: muted
          ? new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString()
          : "",
      };
      const res = await updateChatMember(
        chatId,
        currentUserMember.id,
        memberData
      );
      if (res.success) {
        setIsMuted(muted);
      } else {
        alert(res.error || "Failed to update mute status");
      }
    } catch (error) {
      console.error("Mute toggle error:", error);
      alert("Failed to update mute status. Please try again.");
    }
  };

  const handleLeaveChat = async () => {
    try {
      if (!currentUserMember) {
        alert("Unable to leave chat. Please refresh and try again.");
        return;
      }

      const res = await deleteChatMember(chatId, currentUserMember.user_id);
      if (res.success) {
        setConfirmLeaveChat(false);
        onClose();
        if (typeof orgChannelOptions?.onChatLeft === "function") {
          orgChannelOptions.onChatLeft(chatId);
        } else {
          window.location.reload();
        }
      } else {
        alert(res.error || "Failed to leave chat");
      }
    } catch (error) {
      console.error("Leave chat error:", error);
      alert("Failed to leave chat. Please try again.");
    }
  };

  const handleDeleteChat = async () => {
    try {
      const res = await deleteChat(chatId);
      if (res.success) {
        setConfirmDeleteChat(false);
        onClose();
        if (typeof orgChannelOptions?.onChatDeleted === "function") {
          orgChannelOptions.onChatDeleted(chatId);
        } else {
          window.location.reload();
        }
      } else {
        alert(res.error || "Failed to delete chat");
      }
    } catch (error) {
      console.error("Delete chat error:", error);
      alert("Failed to delete chat. Please try again.");
    }
  };

  const handleRemoveMember = async (member) => {
    try {
      const res = await deleteChatMember(chatId, member.user_id);
      if (res.success) {
        setConfirmRemoveMember(null);
        handleMembersUpdate();
      } else {
        alert(res.error || "Failed to remove member");
      }
    } catch (error) {
      console.error("Remove member error:", error);
      alert("Failed to remove member. Please try again.");
    }
  };

  const handleMembersUpdate = async () => {
    if (!chatId || !userId || !isGroupChat) return;

    try {
      const res = orgId
        ? await getOrganizationChats(orgId, userId)
        : await getChats(userId);
      if (res.success && res.data) {
        const currentChat = res.data.find((c) => c.id === chatId);
        if (currentChat) {
          applyChatMembers(currentChat);
        }
      }
    } catch (error) {
      console.error("Error refreshing members:", error);
    }
  };

  const canAddMembers = isOrgChannel ? !isDefaultChannel : isAdmin;
  // Org non-default: only channel admin can remove. Default General: org admin page only.
  const canRemoveMembers = isOrgChannel
    ? isAdmin && !isDefaultChannel
    : isAdmin;
  const canLeave = isGroupChat && !isDefaultChannel;
  const canDelete = isAdmin && !isDefaultChannel;

  if (!chatInfo) {
    return (
      <Dialog
        open
        onOpenChange={(open) => {
          if (!open) onClose();
        }}
      >
        <DialogContent className="flex max-h-[85vh] min-h-[200px] flex-col p-0 sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Chat details</DialogTitle>
            <DialogDescription>Loading conversation information.</DialogDescription>
          </DialogHeader>
          <div className="flex flex-1 items-center justify-center px-6 py-12 text-sm text-ui-muted-foreground">
            Loading chat info…
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  const defaultImage = isGroupChat ? defaultGroupPfpUrl : defaultPfpUrl;
  const displayImage = imageError
    ? defaultImage
    : (isGroupChat ? chatInfo.image_url : chatInfo.other_pfp) || defaultImage;
  const chatName = isGroupChat
    ? chatInfo.name
    : chatInfo.other_name || chatInfo.name;
  const chatTypeLabel =
    chatInfo.chat_type || chatInfo.type || (isGroupChat ? "group" : "direct");

  return (
    <>
      <Dialog
        open
        onOpenChange={(open) => {
          if (!open) onClose();
        }}
      >
        <DialogContent
          className={cn(
            "flex h-[min(560px,85vh)] max-h-[85vh] flex-col gap-0 p-0 sm:max-w-md"
          )}
          onPointerDownOutside={(e) => {
            if (confirmRemoveMember || confirmLeaveChat || confirmDeleteChat) {
              e.preventDefault();
            }
          }}
        >
          <DialogHeader className="shrink-0 space-y-0 border-0 px-6 pb-0 pt-6">
            <div className="flex items-start justify-between gap-3">
              <div>
                <DialogTitle className="text-xl font-semibold tracking-tight">
                  Chat details
                </DialogTitle>
                <DialogDescription className="mt-1.5">
                  View members and manage this conversation.
                </DialogDescription>
              </div>
              <DialogClose asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="shrink-0 text-ui-muted-foreground"
                  aria-label="Close"
                >
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </Button>
              </DialogClose>
            </div>
          </DialogHeader>

          <Tabs
            value={activeTab}
            onValueChange={setActiveTab}
            className="flex min-h-0 flex-1 flex-col px-6 pb-6 pt-4"
          >
            <TabsList className="mb-4 grid w-full shrink-0 grid-cols-2">
              <TabsTrigger value="info">Info</TabsTrigger>
              <TabsTrigger value="settings">Settings</TabsTrigger>
            </TabsList>

            <div className="relative min-h-0 flex-1">
              <TabsContent
                value="info"
                forceMount
                className="absolute inset-0 m-0 flex min-h-0 flex-col overflow-hidden p-0 data-[state=inactive]:hidden"
              >
                <ScrollArea className="h-full min-h-0 pr-3">
                  <div className="space-y-6 pb-1">
                    <div className="flex flex-col items-center text-center">
                      <Avatar className="h-20 w-20 border border-ui-border shadow-sm">
                        <AvatarImage
                          className="object-cover"
                          src={displayImage}
                          alt={chatName}
                          onError={() => setImageError(true)}
                        />
                        <AvatarFallback className="text-lg">
                          {chatName?.slice(0, 2)?.toUpperCase() || "?"}
                        </AvatarFallback>
                      </Avatar>
                      <h4 className="mt-3 text-lg font-semibold text-ui-foreground">
                        {chatName}
                      </h4>
                      {isGroupChat && (
                        <p className="mt-1 text-sm text-ui-muted-foreground">
                          {chatInfo.member_count || members.length || 0} members
                        </p>
                      )}
                    </div>

                    <div className="rounded-md border border-ui-border bg-ui-background px-4 py-3">
                      <div className="flex items-center justify-between gap-4 text-sm">
                        <span className="text-ui-muted-foreground">Type</span>
                        <span className="font-medium text-ui-foreground">
                          {isGroupChat ? "Group chat" : "Direct message"}
                        </span>
                      </div>
                      <Separator className="my-3" />
                      <div className="flex items-center justify-between gap-4 text-sm">
                        <span className="text-ui-muted-foreground">Created</span>
                        <span className="font-medium text-ui-foreground">
                          {new Date(
                            chatInfo.created_at || Date.now()
                          ).toLocaleDateString()}
                        </span>
                      </div>
                      <Separator className="my-3" />
                      <div className="flex items-center justify-between gap-4 text-sm">
                        <span className="text-ui-muted-foreground">Identifier</span>
                        <span className="max-w-[55%] truncate font-medium capitalize text-ui-foreground">
                          {String(chatTypeLabel)}
                        </span>
                      </div>
                    </div>

                    {isGroupChat && (
                      <div>
                        <h4 className="mb-2 text-sm font-semibold text-ui-foreground">
                          Members
                        </h4>
                        {loadingMembers ? (
                          <div className="py-8 text-center text-sm text-ui-muted-foreground">
                            Loading members…
                          </div>
                        ) : (
                          <MemberList
                            members={members}
                            canAddMembers={canAddMembers}
                            canRemoveMembers={canRemoveMembers}
                            currentUserId={userId}
                            chatId={chatId}
                            onMembersUpdate={handleMembersUpdate}
                            onConfirmRemove={setConfirmRemoveMember}
                            candidateUsers={orgChannelOptions?.candidateUsers}
                          />
                        )}
                      </div>
                    )}
                  </div>
                </ScrollArea>
              </TabsContent>

              <TabsContent
                value="settings"
                forceMount
                className="absolute inset-0 m-0 flex min-h-0 flex-col overflow-hidden p-0 data-[state=inactive]:hidden"
              >
                <ScrollArea className="h-full min-h-0 pr-3">
                  <div className="space-y-6 pb-1">
                    <div className="rounded-md border border-ui-border bg-ui-background px-4 py-4">
                      <h4 className="text-sm font-semibold text-ui-foreground">
                        Notifications
                      </h4>
                      <div className="mt-3 flex items-center gap-3 text-sm text-ui-foreground">
                        <Checkbox
                          id="chat-mute-notifications"
                          checked={isMuted}
                          onCheckedChange={handleMuteToggle}
                        />
                        <label
                          htmlFor="chat-mute-notifications"
                          className="cursor-pointer text-ui-muted-foreground"
                        >
                          Mute notifications
                        </label>
                      </div>
                    </div>

                    <div className="space-y-2">
                      {canLeave && (
                        <Button
                          type="button"
                          variant="outline"
                          className="w-full border-delete text-delete hover:bg-highlight"
                          onClick={() => setConfirmLeaveChat(true)}
                        >
                          Leave chat
                        </Button>
                      )}
                      {canDelete && (
                        <Button
                          type="button"
                          variant="destructive"
                          className="w-full"
                          onClick={() => setConfirmDeleteChat(true)}
                        >
                          Delete chat
                        </Button>
                      )}
                    </div>
                  </div>
                </ScrollArea>
              </TabsContent>
            </div>
          </Tabs>
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(confirmRemoveMember)}
        onOpenChange={(open) => {
          if (!open) setConfirmRemoveMember(null);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Remove member?</DialogTitle>
            <DialogDescription>
              {confirmRemoveMember
                ? `Are you sure you want to remove ${confirmRemoveMember.name || confirmRemoveMember.username || "this member"} from this channel?`
                : null}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={() => setConfirmRemoveMember(null)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={() => confirmRemoveMember && handleRemoveMember(confirmRemoveMember)}
            >
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={confirmLeaveChat}
        onOpenChange={(open) => {
          if (!open) setConfirmLeaveChat(false);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Leave chat?</DialogTitle>
            <DialogDescription>
              You will no longer receive messages from this group. Continue?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={() => setConfirmLeaveChat(false)}
            >
              Cancel
            </Button>
            <Button type="button" variant="destructive" onClick={handleLeaveChat}>
              Leave
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={confirmDeleteChat}
        onOpenChange={(open) => {
          if (!open) setConfirmDeleteChat(false);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete chat?</DialogTitle>
            <DialogDescription>
              This cannot be undone. All messages and member data will be permanently deleted.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={() => setConfirmDeleteChat(false)}
            >
              Cancel
            </Button>
            <Button type="button" variant="destructive" onClick={handleDeleteChat}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default ChatInfo;
